// Apolo IDE — usePlanChat (sprint plan-tab-v1, E5).
//
// Conversa com o copiloto do Plan. System prompt enriquecido com nome do
// plano + resumo de blocks visíveis (até 32k chars). Usa /api/chat existente
// (não-streaming — pra v1 isso é suficiente; streaming chega em sprint v2).

import { useCallback, useState } from 'react';
import { apiFetch } from '../lib/api.js';

// E3-WS2-T01 — keyless-resilient default. The account has DeepSeek (no Anthropic
// key) and the Worker has no global LLM key, so the old Opus default 502'd the
// Plan copilot. google/gemini-2.5-flash is the registry-resolvable account
// default (matches useAgentStore.DEFAULT_MODEL; switched 2026-06-15 from
// deepseek/deepseek-v4-pro). Exported so a test can assert it stays
// registry-resolvable (the exact regression that broke brainRouter).
export const DEFAULT_MODEL = 'google/gemini-3-flash-preview';

function summarizeBlocks(blocks, maxChars = 12000) {
  if (!Array.isArray(blocks) || blocks.length === 0) return '(canvas vazio)';
  const lines = blocks.map((b) => {
    const payload = b.payload || {};
    const sample = (payload.body || payload.text || payload.title || payload.url || payload.skill_name || payload.label || '').slice(0, 200);
    return `- [${b.kind}] ${sample || '(sem conteúdo)'}`;
  });
  let out = lines.join('\n');
  if (out.length > maxChars) out = out.slice(0, maxChars) + '\n…(truncado)';
  return out;
}

function buildSystemPrompt({ plan, blocks }) {
  return [
    'Você é o copiloto do Plan tab no Apolo IDE.',
    'Ajuda o usuário a transformar uma ideia em sprint executável (discovery → arquitetura → DB → delivery → segurança).',
    'O usuário trabalha num canvas com blocos arrastáveis. Você sugere o próximo bloco, escreve markdown, lista skills, refina copy.',
    'Quando o usuário pedir, gere blocos novos como JSON em ```json ... ``` no formato { "kind": "text|sticky|heading|todo|skill|...", "payload": {...} }.',
    '',
    `Plano ativo: "${plan?.name || 'sem nome'}" (status: ${plan?.status || 'draft'}).`,
    '',
    `Canvas atual (${blocks?.length || 0} blocos):`,
    summarizeBlocks(blocks),
  ].join('\n');
}

export function usePlanChat({ plan, blocks }) {
  const [messages, setMessages] = useState([]); // [{ role: 'user'|'assistant', content }]
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const send = useCallback(async (text) => {
    const userMsg = { role: 'user', content: text };
    const history = [...messages, userMsg];
    setMessages(history);
    setSending(true);
    setError(null);
    const sys = buildSystemPrompt({ plan, blocks });
    const res = await apiFetch('/api/chat', {
      method: 'POST',
      body: {
        model: DEFAULT_MODEL,
        system: sys,
        messages: history.map((m) => ({ role: m.role, content: m.content })),
        max_tokens: 2048,
      },
    });
    setSending(false);
    if (!res.ok) {
      setError(res.error || 'Falha no chat');
      return { ok: false };
    }
    // Aceita formato Anthropic { content: [{text}] } ou direct { text }.
    const data = res.data || {};
    const content = (data?.content?.[0]?.text) || data?.text || data?.message || '(sem resposta)';
    setMessages((prev) => [...prev, { role: 'assistant', content }]);
    return { ok: true, content };
  }, [messages, plan, blocks]);

  const reset = useCallback(() => { setMessages([]); setError(null); }, []);

  return { messages, sending, error, send, reset };
}
